import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Card, Checkbox, Modal, Progress, Radio, Result, Segmented, Space, Spin, Tag, Tooltip, Typography, Upload, message,
} from 'antd';
import Table from 'components/Common/Table';
import {
  CheckCircleOutlined, CloseCircleOutlined, DownloadOutlined, ExclamationCircleOutlined,
  ExperimentOutlined, InboxOutlined, UploadOutlined, WarningOutlined,
} from '@ant-design/icons';
import * as actions from 'store/actions/AuthActions';
import useIsMobile from 'hooks/useIsMobile';

const { Dragger } = Upload;
const { Text } = Typography;

const MAX_FILE_SIZE_MB = 10;

const STATUS = {
  valid: { label: 'Valid', color: 'success', icon: <CheckCircleOutlined /> },
  invalid: { label: 'Invalid', color: 'error', icon: <CloseCircleOutlined /> },
  duplicate: { label: 'Duplicate', color: 'warning', icon: <WarningOutlined /> },
  test: { label: 'Test lead', color: 'default', icon: <ExperimentOutlined /> },
};

// Rows the user may tick for a given duplicate mode.
const isImportable = (row, duplicateMode) =>
  row.status === 'valid' || (row.status === 'duplicate' && duplicateMode === 'update');

const POLL_MS = 1500;

const errorText = (err) => err?.response?.data?.error || err?.message || 'Something went wrong';

/**
 * Upload a lead file - the filled sample workbook, any spreadsheet with
 * recognisable headers, or a Meta lead-ads export (English / Hindi / Marathi) -
 * review every row's validation result, then import.
 */
const ImportLeadsModal = ({ open, onClose, onSuccess }) => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [filter, setFilter] = useState('all');
  const [duplicateMode, setDuplicateMode] = useState('skip');
  const [selectedRows, setSelectedRows] = useState([]);
  const [extraKeys, setExtraKeys] = useState([]);
  const [result, setResult] = useState(null);
  const [job, setJob] = useState(null); // latest polled job: status + progress
  const pollTimer = useRef(null);

  const stopPolling = () => {
    clearTimeout(pollTimer.current);
    pollTimer.current = null;
  };
  useEffect(() => stopPolling, []);

  // Poll the import job until `isDone(job)`; both the row checks and the
  // import itself run in the background on the server.
  const pollJob = (jobId, isDone) =>
    new Promise((resolve, reject) => {
      const tick = async () => {
        try {
          const { data } = await actions.getLeadsImportStatus(jobId);
          setJob(data);
          if (data.status === 'FAILED') return reject(new Error(data.error || 'Import failed'));
          if (isDone(data)) return resolve(data);
          pollTimer.current = setTimeout(tick, POLL_MS);
        } catch (err) {
          reject(err);
        }
      };
      pollTimer.current = setTimeout(tick, POLL_MS);
    });

  const reset = () => {
    stopPolling();
    setJob(null);
    setPreview(null);
    setUploadError('');
    setFilter('all');
    setDuplicateMode('skip');
    setSelectedRows([]);
    setExtraKeys([]);
    setResult(null);
  };

  const handleClose = () => {
    if (importing) {
      message.info('The import keeps running in the background. The leads list refreshes when you reopen it.');
    }
    reset();
    setImporting(false);
    onClose();
  };

  const handleDownloadSample = async () => {
    setDownloading(true);
    try {
      await actions.downloadLeadsImportSample();
    } catch (err) {
      message.error('Failed to download sample file');
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx'].includes(ext)) {
      setUploadError('Please upload a .csv or .xlsx file');
      return false;
    }
    if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
      setUploadError(`File must be smaller than ${MAX_FILE_SIZE_MB}MB`);
      return false;
    }

    reset();
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data: queued } = await actions.previewLeadsImport(formData);
      setJob(queued);
      const done = await pollJob(queued.job_id, (j) => j.status === 'PREVIEW_READY');
      const data = done.preview;
      setPreview(data);
      setExtraKeys((data.extra_columns || []).map((c) => c.key));
      setSelectedRows(data.rows.filter((r) => isImportable(r, 'skip')).map((r) => r.row));
    } catch (err) {
      setUploadError(errorText(err));
    } finally {
      setLoading(false);
    }
    return false;
  };

  const handleDuplicateMode = (mode) => {
    setDuplicateMode(mode);
    const duplicates = preview.rows.filter((r) => r.status === 'duplicate').map((r) => r.row);
    setSelectedRows((prev) =>
      mode === 'update'
        ? Array.from(new Set([...prev, ...duplicates]))
        : prev.filter((row) => !duplicates.includes(row))
    );
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const { data: queued } = await actions.confirmLeadsImport({
        job_id: preview.job_id,
        duplicate_mode: duplicateMode,
        rows: selectedRows,
        extra_keys: extraKeys,
      });
      setJob(queued);
      const done = await pollJob(queued.job_id, (j) => j.status === 'COMPLETED');
      setResult(done.result);
      if (done.result.created || done.result.updated) onSuccess?.();
    } catch (err) {
      message.error(errorText(err));
    } finally {
      setImporting(false);
    }
  };

  const rows = useMemo(() => {
    if (!preview) return [];
    return filter === 'all' ? preview.rows : preview.rows.filter((r) => r.status === filter);
  }, [preview, filter]);

  const columns = useMemo(() => {
    if (!preview) return [];
    const teamSizeLabels = preview.team_size_labels || {};

    // Cell text, tinted and explained when the row has an error/warning on it.
    const renderCell = (field, format) => (_, row) => {
      const value = row.data[field];
      const errors = row.errors.filter((e) => e.field === field);
      const warnings = row.warnings.filter((w) => w.field === field);
      const content = format ? format(value) : value || <Text type="secondary">—</Text>;
      if (!errors.length && !warnings.length) return content;
      return (
        <Tooltip title={[...errors, ...warnings].map((i) => i.message).join('\n')}>
          <Text type={errors.length ? 'danger' : 'warning'}>
            {errors.length ? <CloseCircleOutlined /> : <ExclamationCircleOutlined />}{' '}
            {value || (errors.length ? 'Missing' : '—')}
          </Text>
        </Tooltip>
      );
    };

    const title = (f) => (
      <Tooltip title={f.note}>
        {f.label}
        {f.required && <Text type="danger"> *</Text>}
      </Tooltip>
    );

    const formats = {
      team_size: (v) => (v ? teamSizeLabels[v] || v : <Text type="secondary">—</Text>),
      products: (v) =>
        v && v.length ? v.map((p) => <Tag key={p}>{p}</Tag>) : <Text type="secondary">—</Text>,
      notes: (v) =>
        v ? (
          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{v}</span>}>
            <Text ellipsis style={{ maxWidth: 160 }}>{v}</Text>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
      business_challenge: (v) =>
        v ? <Text ellipsis={{ tooltip: v }} style={{ maxWidth: 200 }}>{v}</Text> : <Text type="secondary">—</Text>,
    };

    return [
      { title: 'Row', dataIndex: 'row', width: 60, fixed: 'left' },
      {
        title: 'Status',
        dataIndex: 'status',
        width: 130,
        fixed: 'left',
        render: (status, row) => {
          const s = STATUS[status];
          const issues = [
            ...row.errors.map((e) => e.message),
            ...row.warnings.map((w) => w.message),
            row.duplicate_of &&
              `Matches existing lead ${row.duplicate_of.lead_number || ''} ${row.duplicate_of.name}`.trim(),
          ].filter(Boolean);
          const tag = <Tag color={s.color} icon={s.icon}>{s.label}</Tag>;
          return issues.length ? (
            <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{issues.join('\n')}</div>}>
              {tag}
            </Tooltip>
          ) : tag;
        },
      },
      ...preview.fields.map((f) => ({
        title: title(f),
        key: f.field,
        width: f.field === 'business_challenge' ? 220 : 150,
        render: renderCell(f.field, formats[f.field]),
      })),
    ];
  }, [preview]);

  const summary = preview?.summary;
  const segmentOptions = summary
    ? [
        { label: `All (${summary.total})`, value: 'all' },
        ...Object.entries(STATUS).map(([key, s]) => ({ label: `${s.label} (${summary[key]})`, value: key })),
      ]
    : [];

  const requiredFields = preview?.fields.filter((f) => f.required) || [];

  const footer = result
    ? [<Button key="close" type="primary" onClick={handleClose}>Close</Button>]
    : preview
      ? [
          <Button key="another" onClick={reset} disabled={importing}>Upload another file</Button>,
          <Button key="cancel" onClick={handleClose}>{importing ? 'Close' : 'Cancel'}</Button>,
          <Button
            key="import"
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={importing}
            disabled={!selectedRows.length}
            onClick={handleImport}
          >
            Import {selectedRows.length} lead{selectedRows.length === 1 ? '' : 's'}
          </Button>,
        ]
      : [<Button key="cancel" onClick={handleClose}>Cancel</Button>];

  return (
    <Modal
      title={
        <Space>
          <UploadOutlined />
          <span>Import Leads</span>
        </Space>
      }
      open={open}
      onCancel={handleClose}
      width={isMobile ? '100%' : preview ? 1200 : 720}
      footer={footer}
      destroyOnClose
    >
      {result ? (
        <Result
          status={result.failed ? 'warning' : 'success'}
          title="Import finished"
          subTitle={
            <Space wrap>
              <Tag color="success">{result.created} created</Tag>
              <Tag color="processing">{result.updated} updated</Tag>
              <Tag>{result.skipped} skipped</Tag>
              {result.failed > 0 && <Tag color="error">{result.failed} failed</Tag>}
            </Space>
          }
          extra={
            result.errors?.length > 0 && (
              <Table
                size="small"
                rowKey={(e) => `${e.row}-${e.error}`}
                dataSource={result.errors}
                columns={[
                  { title: 'Row', dataIndex: 'row', width: 80 },
                  { title: 'Error', dataIndex: 'error' },
                ]}
                pagination={false}
                scroll={{ y: 200 }}
              />
            )
          }
        />
      ) : !preview ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Card size="small" title="Step 1: Get the file">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text type="secondary">
                Download the sample workbook and fill it in (its Products column is a dropdown of your
                products), or upload your own sheet or a leads CSV from Meta Ads Manager as it is (English,
                Hindi or Marathi forms).
              </Text>
              <Button icon={<DownloadOutlined />} loading={downloading} onClick={handleDownloadSample}>
                Download Sample File
              </Button>
            </Space>
          </Card>

          <Card size="small" title="Step 2: Upload and review">
            <Dragger
              accept=".csv,.xlsx"
              multiple={false}
              showUploadList={false}
              beforeUpload={handleUpload}
              disabled={loading}
            >
              {loading ? (
                <div style={{ padding: 20 }}>
                  {job?.rows_total ? (
                    <Progress
                      type="circle"
                      size={80}
                      percent={Math.floor((job.rows_processed * 100) / job.rows_total)}
                    />
                  ) : (
                    <Spin size="large" />
                  )}
                  <p style={{ marginTop: 16 }}>
                    {job?.rows_total
                      ? `Checking rows… ${job.rows_processed} of ${job.rows_total}`
                      : 'Uploading and reading the file…'}
                  </p>
                </div>
              ) : (
                <>
                  <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                  <p className="ant-upload-text">Click or drag the file here</p>
                  <p className="ant-upload-hint">.csv or .xlsx, up to {MAX_FILE_SIZE_MB}MB. Nothing is saved until you confirm.</p>
                </>
              )}
            </Dragger>
            {uploadError && <Alert style={{ marginTop: 12 }} type="error" showIcon message={uploadError} />}
          </Card>

          <Alert
            type="info"
            showIcon
            message="Required for each lead"
            description="Name (full name or first name), and a phone number or an email. Everything else is optional; columns the CRM doesn't know can be kept as additional details."
          />
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type={summary.invalid ? 'warning' : 'success'}
            showIcon
            message={
              <Space wrap>
                <Text strong>{preview.file_name}</Text>
                <Tag color="success">{summary.valid} valid</Tag>
                <Tag color="error">{summary.invalid} invalid</Tag>
                <Tag color="warning">{summary.duplicate} duplicate</Tag>
                <Tag>{summary.test} test</Tag>
                {summary.warnings > 0 && <Text type="secondary">{summary.warnings} row(s) with warnings</Text>}
              </Space>
            }
            description={
              <Text type="secondary">
                Required: {requiredFields.map((f) => f.label).join(', ')}
                <Text type="danger"> *</Text> (phone or email is enough). Invalid and test rows are never imported.
                Hover a status or a highlighted cell to see why.
              </Text>
            }
          />

          {importing && (
            <Alert
              type="info"
              showIcon
              message={
                job?.rows_total
                  ? `Importing… ${job.rows_processed} of ${job.rows_total} rows`
                  : 'Starting import…'
              }
              description={
                <>
                  <Progress
                    percent={job?.rows_total ? Math.floor((job.rows_processed * 100) / job.rows_total) : 0}
                    status="active"
                  />
                  <Text type="secondary">This runs in the background - you can close this window.</Text>
                </>
              }
            />
          )}

          {preview.extra_columns?.length > 0 && (
            <Card size="small" title="Additional details: other columns to keep on each lead">
              <Checkbox.Group
                value={extraKeys}
                onChange={setExtraKeys}
                options={preview.extra_columns.map((c) => ({ label: c.label, value: c.key }))}
              />
            </Card>
          )}

          <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
            <Segmented options={segmentOptions} value={filter} onChange={setFilter} />
            {summary.duplicate > 0 && (
              <Space>
                <Text>Duplicates:</Text>
                <Radio.Group
                  value={duplicateMode}
                  onChange={(e) => handleDuplicateMode(e.target.value)}
                  optionType="button"
                  options={[
                    { label: 'Skip', value: 'skip' },
                    { label: 'Update existing', value: 'update' },
                  ]}
                />
              </Space>
            )}
          </Space>

          <Table
            size="small"
            bordered
            rowKey="row"
            dataSource={rows}
            columns={columns}
            rowSelection={{
              selectedRowKeys: selectedRows,
              onChange: setSelectedRows,
              preserveSelectedRowKeys: true,
              getCheckboxProps: (row) => ({ disabled: !isImportable(row, duplicateMode) }),
            }}
            onRow={(row) => (row.status === 'invalid' ? { style: { background: '#fff2f0' } } : {})}
            pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
            scroll={{ x: 'max-content', y: 'calc(100vh - 480px)' }}
          />
        </Space>
      )}
    </Modal>
  );
};

export default ImportLeadsModal;
